/**
 * Plausible Analytics — typed event wrapper.
 *
 * Two surfaces:
 *   1. trackEvent(name, props?) — programmatic, for events you fire from
 *      code (e.g. on a successful API response).
 *   2. plausibleEventClass(name, props?) — emits the `class="plausible-event-name=..."`
 *      string consumed by the tracker's tagged-events extension, with the
 *      same compile-time event-name check as trackEvent.
 *
 * Both go through PlausibleEventName, so a typo on either path is a build
 * error. To add a new event: append it to PlausibleEventName below, then
 * register it as a Goal in the Plausible dashboard (Settings → Goals →
 * Add goal → Custom event → paste the exact name).
 *
 * UTM attribution: do NOT pass utm_source as a prop. Plausible joins every
 * event to its visit's UTM context server-side, so the dashboard already
 * splits goal conversions by UTM source/medium/campaign when you apply the
 * corresponding filter. Passing utm as a prop would double-store it without
 * adding any analytic value.
 *
 * Built-ins NOT in the union: the outbound-links extension fires its own
 * 'Outbound Link: Click' event with a {url} prop on every off-host anchor
 * click. We never call that name from code — listing it would falsely
 * suggest it's manually fired.
 */

export type PlausibleEventName =
  // Per-step registration funnel — fires on each successful step advance
  // (email → code → profile → consent). Use {step, durationBucket} props.
  // Funnel = breakdown by `step`; per-step timing = breakdown by
  // `durationBucket` filtered to a single step. Drop-off between steps tells
  // us where users abandon without us having to fire unreliable unload events.
  | 'Signup Step'
  // Terminal registration conversion — fires on successful POST /auth/register
  // (PENDING → ACTIVE). Use {durationBucket} prop for the total flow time.
  // Register as a Goal in Plausible.
  | 'Signup'
  // Returning-user weekly consent submit — fires on successful
  // POST /event/rules/consent from /weekly-rules. Use {weekNumber,
  // durationBucket} props. Register as a Goal in Plausible.
  | 'Weekly Consent'
  // Hero stream-platform CTAs (YouTube / Twitch). Tagged on the anchors so
  // we capture intent even when the outbound-link auto-event is suppressed
  // (e.g. some adblockers selectively block POSTs but not page interaction).
  | 'Watch Click'
  // AppBar social icons (Facebook / Instagram / TikTok / X / YouTube /
  // Twitch). Same rationale as Watch Click — doubles up on the
  // outbound-links auto-event so adblocker-suppressed POSTs don't blind us
  // to social-channel click-through from the bar. Use {platform} prop.
  | 'Social Click'
  // PLAY NOW CTA in the Hero. Primary conversion intent on the home page —
  // fires whether or not the destination route exists yet. Use {location}
  // prop (e.g. 'hero', 'archive') if the same event ships from multiple
  // surfaces later.
  | 'Play Click'
  // Showreel video play in the Archive section. Fired from the click-to-load
  // facade's play handler (ArchiveSection's ShowreelEmbed) — the click that
  // swaps in the YouTube iframe is the play signal, so no Player JS is needed.
  // Use {location} prop if a second showreel ships on another surface.
  | 'Video Play'
  // "Gamerhood Playlist" CTA in the Archive section — links to the State
  // Farm YouTube playlist. Tagged so we keep the click signal even when
  // the outbound-links auto-event is suppressed. Use {platform} prop in
  // case a second-platform playlist (Twitch / etc.) ships later.
  | 'Playlist Click'
  // Week-tab click on the /leaderboard page. Fires on every tab change,
  // including the implicit initial select (e.g. landing on Week 3 because
  // event state says that's the current week). Use {week} prop (string,
  // e.g. '1'..'5') for breakdown.
  | 'Leaderboard Tab Click';

type PlausiblePropValue = string | number | boolean;

type PlausibleQueueEntry = [string, { props?: Record<string, PlausiblePropValue> }?];

type PlausibleFn = ((
  name: string,
  options?: { props?: Record<string, PlausiblePropValue> },
) => void) & {
  q?: PlausibleQueueEntry[];
};

declare global {
  interface Window {
    plausible?: PlausibleFn;
  }
}

/**
 * Fire a Plausible custom event.
 *
 * Safe to call before the tracker script loads (events queue via the stub
 * in index.html and replay once script.js executes) and safe to call when
 * Plausible isn't loaded at all (opt-out, ad-block, dev env without
 * VITE_PLAUSIBLE_DOMAIN) — the call no-ops without throwing.
 *
 * Defense-in-depth gate: the Plausible loader IIFE in index.html already
 * refuses to inject the script under GPC / gh_consent.analytics=false /
 * SF _Analytics=0, so trackEvent normally never even sees `window.plausible`
 * as a real function in those cases. This check protects the edge where
 * the script was loaded (e.g., analytics was granted at page load) and the
 * user then denied via /privacy-settings mid-session — syncPlausible
 * removes the DOM script but the earlier `window.plausible` stub or
 * function reference could still enqueue events for the next pageload.
 */
export function trackEvent(
  name: PlausibleEventName,
  props?: Record<string, PlausiblePropValue>,
): void {
  try {
    if (typeof window === 'undefined' || typeof window.plausible !== 'function') return;
    if (isAnalyticsDenied()) return;
    window.plausible(name, props ? { props } : undefined);
  } catch {
    /* analytics must never throw into the host app */
  }
}

/**
 * Read the current effective analytics state directly — mirrors the gate
 * chain in the index.html Plausible IIFE and in consent.ts:computeEffectivePrefs.
 * Inline rather than imported so plausible.ts stays self-contained and
 * doesn't create a circular dependency with consent.ts (which imports the
 * event-name union for its own logging).
 */
function isAnalyticsDenied(): boolean {
  try {
    if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true) {
      return true;
    }
  } catch { /* navigator access blocked */ }

  try {
    const raw = window.localStorage.getItem('gh_consent');
    if (raw) {
      const saved = JSON.parse(raw) as { prefs?: { analytics?: unknown } } | null;
      if (saved && saved.prefs && saved.prefs.analytics === false) return true;
    }
  } catch { /* localStorage blocked or parse error */ }

  try {
    const country = window.sessionStorage.getItem('gh_viewer_country');
    if (country === 'US') {
      const cookies = document.cookie || '';
      for (const raw of cookies.split(';')) {
        const t = raw.trim();
        const prefix = 'STATEFARM_ENSIGHTEN_PRIVACY_Analytics=';
        if (t.indexOf(prefix) === 0) {
          if (t.substring(prefix.length) === '0') return true;
          break;
        }
      }
    }
  } catch { /* cookie or sessionStorage read blocked */ }

  return false;
}

/**
 * Build the className string for declarative click-tracking via the
 * tagged-events extension.
 *
 *   <a className={plausibleEventClass('Watch Click', { platform: 'YouTube' })} ...>
 *
 * Emits: "plausible-event-name=Watch+Click plausible-event-platform=YouTube"
 *
 * Spaces in names/values are encoded as `+` per the Plausible tracker's
 * class parser. Combine with other Tailwind classes via template strings.
 */
export function plausibleEventClass(
  name: PlausibleEventName,
  props?: Record<string, string>,
): string {
  const parts = [`plausible-event-name=${encodeForClass(name)}`];
  if (props) {
    for (const [key, value] of Object.entries(props)) {
      parts.push(`plausible-event-${key}=${encodeForClass(value)}`);
    }
  }
  return parts.join(' ');
}

function encodeForClass(s: string): string {
  return s.replace(/ /g, '+');
}
